import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Folder, FolderOpen, FileText, Download, Trash2,
  ChevronRight, Search, X as XIcon, Wand2, Eye,
} from "lucide-react";

function bytesToHuman(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Given a full filename (may include folder path), derive a stable
 * "unique image key" so that companion files count as ONE image:
 *   IMG_0192.CR2            → images/IMG_0192
 *   IMG_0192.CR2.preview.jpg → images/IMG_0192
 *   IMG_0192.xmp            → images/IMG_0192
 * Folder path is preserved so identical filenames in different
 * subfolders count independently.
 */
function uniqueImageKey(filename) {
  if (!filename) return "";
  const slash = filename.lastIndexOf("/");
  const dir = slash >= 0 ? filename.substring(0, slash) : "";
  let name = slash >= 0 ? filename.substring(slash + 1) : filename;
  // Peel `.preview.jpg` (case-insensitive) FIRST — it hides the
  // underlying original extension.
  name = name.replace(/\.preview\.jpe?g$/i, "");
  // Then strip the last extension.
  const dot = name.lastIndexOf(".");
  if (dot > 0) name = name.substring(0, dot);
  return dir ? `${dir}/${name}` : name;
}

/**
 * Build a nested tree from a flat file list where each `filename` may
 * contain forward-slash directory separators. Files with no ``/`` end
 * up in the root, deeper nesting produces child ``dirs`` nodes.
 *
 * ``totalCount`` = raw file count (CR2 + .preview.jpg + .xmp all counted).
 * ``uniqueCount`` = de-duplicated "images" count — companion files
 * (previews, sidecars) collapse into their parent CR2. This is what the
 * folder header renders because "36 files" for 12 photos is confusing.
 */
function buildFileTree(files) {
  const root = { files: [], dirs: {}, totalCount: 0, totalSize: 0, _keys: new Set() };
  for (const f of files) {
    const parts = String(f.filename || "").split("/");
    const key = uniqueImageKey(f.filename);
    let node = root;
    root.totalCount += 1;
    root.totalSize += f.size || 0;
    root._keys.add(key);
    for (let i = 0; i < parts.length - 1; i += 1) {
      const seg = parts[i];
      if (!node.dirs[seg]) {
        node.dirs[seg] = { files: [], dirs: {}, totalCount: 0, totalSize: 0, _keys: new Set() };
      }
      node = node.dirs[seg];
      node.totalCount += 1;
      node.totalSize += f.size || 0;
      node._keys.add(key);
    }
    node.files.push(f);
  }
  // Freeze the Set → number so downstream code doesn't touch the Set.
  const finalise = (n) => {
    n.uniqueCount = n._keys.size;
    delete n._keys;
    for (const child of Object.values(n.dirs)) finalise(child);
  };
  finalise(root);
  return root;
}

/**
 * Filter a tree by keeping only files (or folders whose name matches)
 * that contain ``query`` (case-insensitive). Returns a new tree.
 */
function filterFileTree(node, query) {
  const q = query.trim().toLowerCase();
  if (!q) return node;
  const walk = (n, prefix) => {
    const out = { files: [], dirs: {}, totalCount: 0, totalSize: 0, uniqueCount: 0, _keys: new Set() };
    for (const f of n.files) {
      const full = (prefix ? `${prefix}/` : "") + f.filename.split("/").pop();
      if (full.toLowerCase().includes(q)) {
        out.files.push(f);
        out.totalCount += 1;
        out.totalSize += f.size || 0;
        out._keys.add(uniqueImageKey(f.filename));
      }
    }
    for (const [name, child] of Object.entries(n.dirs)) {
      const nextPrefix = prefix ? `${prefix}/${name}` : name;
      const filteredChild = walk(child, nextPrefix);
      if (
        nextPrefix.toLowerCase().includes(q)
        || filteredChild.totalCount > 0
      ) {
        const dirNameHit = nextPrefix.toLowerCase().includes(q)
          && filteredChild.totalCount === 0;
        const kept = dirNameHit ? child : filteredChild;
        out.dirs[name] = kept;
        out.totalCount += kept.totalCount;
        out.totalSize += kept.totalSize;
        // Merge child unique-keys upward; child was either a filtered
        // subtree (has _keys) or a raw pre-built node (has uniqueCount
        // but no _keys — approximate by adding its uniqueCount).
        if (kept._keys) {
          for (const k of kept._keys) out._keys.add(k);
        } else if (kept.uniqueCount) {
          // dirNameHit path: we're keeping the ORIGINAL subtree as-is
          // — we can't inspect its per-file keys without a re-walk, so
          // approximate by adding the subtree's uniqueCount. Slight
          // over-count is possible if two branches share unique keys;
          // in practice unique keys include the folder path so it's
          // exact.
          out.uniqueCount = (out.uniqueCount || 0) + kept.uniqueCount;
        }
      }
    }
    // Convert the local Set into a plain uniqueCount before returning.
    if (out._keys) {
      out.uniqueCount = (out.uniqueCount || 0) + out._keys.size;
      delete out._keys;
    }
    return out;
  };
  return walk(node, "");
}

function FileRow({ file, depth, testidPrefix, canDelete, onDownload, onDelete }) {
  const base = file.filename.split("/").pop();
  return (
    <div
      className="py-1.5 px-1 flex items-center gap-2 text-sm hover:bg-slate-50 rounded-sm transition-colors"
      style={{ paddingLeft: `${depth * 14 + 4}px` }}
      data-testid={`${testidPrefix}-row-${file.filename}`}
    >
      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="truncate font-medium text-slate-800 flex-1">{base}</span>
      <span className="text-xs text-slate-500 tabular-nums shrink-0 font-semibold">
        {bytesToHuman(file.size)}
      </span>
      <div className="flex items-center gap-0.5 shrink-0">
        <Button
          size="icon" variant="ghost"
          onClick={() => onDownload(file.filename)}
          className="h-8 w-8 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-md"
          title="Download"
          data-testid={`${testidPrefix}-download-${file.filename}`}
        >
          <Download className="h-4 w-4" />
        </Button>
        {canDelete && (
          <Button
            size="icon" variant="ghost"
            onClick={() => onDelete(file.filename)}
            className="h-8 w-8 text-slate-500 hover:text-destructive hover:bg-red-50 rounded-md"
            title="Delete"
            data-testid={`${testidPrefix}-delete-${file.filename}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Flatten a tree into an ordered list of rows: each row is either a
 * folder header or a file. Depth-first, folders before files at each
 * level. Only children of open folders are included so collapsed
 * subtrees don't inflate the DOM.
 */
function flattenTree(node, openMap, depth = 0, path = "") {
  const rows = [];
  const dirEntries = Object.entries(node.dirs)
    .sort(([a], [b]) => a.localeCompare(b));
  for (const [name, child] of dirEntries) {
    const childPath = path ? `${path}/${name}` : name;
    const open = openMap[childPath] ?? false;
    rows.push({ kind: "dir", name, path: childPath, node: child, depth, open });
    if (open) {
      rows.push(...flattenTree(child, openMap, depth + 1, childPath));
    }
  }
  for (const f of node.files.slice().sort((a, b) => a.filename.localeCompare(b.filename))) {
    rows.push({ kind: "file", file: f, depth });
  }
  return rows;
}

/**
 * Collapsible + searchable tree view for the uploaded/edited file
 * list. Handles hundreds of nested files without DOM bloat because
 * subfolder rows only render when their parent is expanded.
 */
export default function FileTreeView({
  files, subfolderStats, canDelete, testidPrefix, onDownload, onDelete,
  onAnalyzeFolder,   // optional (path) => void; renders per-folder Analyze btn
  onView,            // optional (path) => void; renders per-folder View btn
}) {
  const [query, setQuery] = useState("");
  const [openMap, setOpenMap] = useState({});
  const tree = useMemo(() => buildFileTree(files), [files]);
  const filtered = useMemo(
    () => (query ? filterFileTree(tree, query) : tree),
    [tree, query],
  );
  // When searching, force everything open so hits are visible.
  const effectiveOpenMap = useMemo(() => {
    if (!query) return openMap;
    const forced = {};
    const walk = (n, prefix) => {
      for (const [name, child] of Object.entries(n.dirs)) {
        const p = prefix ? `${prefix}/${name}` : name;
        forced[p] = true;
        walk(child, p);
      }
    };
    walk(filtered, "");
    return forced;
  }, [query, openMap, filtered]);

  const rows = useMemo(
    () => flattenTree(filtered, effectiveOpenMap),
    [filtered, effectiveOpenMap],
  );

  const hasSubdirs = Object.keys(tree.dirs).length > 0;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {(files.length > 10 || hasSubdirs) && (
        <div className="relative mb-2">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${files.length} files…`}
            className="pl-8 pr-8 h-9 text-sm"
            data-testid={`${testidPrefix}-search`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
              data-testid={`${testidPrefix}-search-clear`}
              title="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-auto max-h-[520px] pr-1">
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 px-2 font-medium">
            {query ? `No files match "${query}".` : "No files."}
          </p>
        ) : (
          rows.map((r) => {
            if (r.kind === "dir") {
              return (
                <div
                  key={`dir:${r.path}`}
                  className="w-full flex items-center gap-2 py-1.5 px-1 hover:bg-slate-50 rounded-sm"
                  style={{ paddingLeft: `${r.depth * 14 + 4}px` }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenMap((m) => ({ ...m, [r.path]: !r.open }))}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    data-testid={`${testidPrefix}-dir-${r.path}`}
                  >
                    <ChevronRight
                      className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${r.open ? "rotate-90" : ""}`}
                    />
                    {r.open ? (
                      <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                    ) : (
                      <Folder className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span className="truncate font-semibold text-slate-800 text-sm">
                      {r.name}
                    </span>
                    <span
                      className="text-[11px] tabular-nums text-slate-500 font-semibold shrink-0 ml-1"
                      title={`${r.node.totalCount} raw files on disk (originals + previews + sidecars)`}
                    >
                      {(r.node.uniqueCount ?? r.node.totalCount)}
                      {" "}
                      image
                      {(r.node.uniqueCount ?? r.node.totalCount) === 1 ? "" : "s"}
                      {r.node.totalSize > 0 && (
                        <span className="ml-1.5 text-slate-400">
                          · {bytesToHuman(r.node.totalSize)}
                        </span>
                      )}
                    </span>
                  </button>
                  {onView && r.node.totalCount > 0 && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(r.path);
                      }}
                      className="h-7 px-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 rounded-md shrink-0"
                      data-testid={`${testidPrefix}-view-${r.path}`}
                      title={`Browse the ${r.node.uniqueCount ?? r.node.totalCount} photos in this folder`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      View
                    </Button>
                  )}
                  {onAnalyzeFolder && r.node.totalCount > 0 && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAnalyzeFolder(r.path);
                      }}
                      className="h-7 px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 rounded-md shrink-0"
                      data-testid={`${testidPrefix}-analyze-${r.path}`}
                      title={`Analyze the ${r.node.uniqueCount ?? r.node.totalCount} photos in this folder in the background`}
                    >
                      <Wand2 className="h-3.5 w-3.5 mr-1" />
                      Analyze
                    </Button>
                  )}
                </div>
              );
            }
            return (
              <FileRow
                key={`file:${r.file.filename}`}
                file={r.file}
                depth={r.depth}
                testidPrefix={testidPrefix}
                canDelete={canDelete}
                onDownload={onDownload}
                onDelete={onDelete}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
